import { useCallback, useMemo, useRef, useState } from "react";

import {
  NotificationFilterForm,
  NotificationFilterFormRef,
  NotificationFilterFormValues,
} from "@components/Forms";
import { NotificationsTable } from "@components/Tables";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";

type Props = {
  initialUserTablePageIndex?: number;
};

export const Notifications = ({ initialUserTablePageIndex = 0 }: Props) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<NotificationFilterFormValues>({
      clientId: undefined,
      notificationType: undefined,
      applicationNameSearch: undefined,
    });

  const filterFormRef = useRef<NotificationFilterFormRef>(null);

  const areFiltersActive = useMemo(() => {
    return Boolean(
      filterValues.clientId ||
        filterValues.notificationType ||
        filterValues.applicationNameSearch,
    );
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues({
      clientId: undefined,
      notificationType: undefined,
      applicationNameSearch: undefined,
    });
  }, []);

  const handleFilterChange = useCallback(
    (newValues: NotificationFilterFormValues) => {
      setFilterValues(newValues);
      // Close the panel when form is submitted (e.g., via Enter key)
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
    }
  }, []);

  return (
    <>
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {areFiltersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={areFiltersActive}
            tooltip="Show filters"
          />
        </Box>
      </PortalHeaderActions>
      <NotificationsTable
        initialPageIndex={initialUserTablePageIndex}
        {...(filterValues.clientId && {
          clientId: filterValues.clientId,
        })}
        {...(filterValues.notificationType && {
          notificationType: filterValues.notificationType,
        })}
        {...(filterValues.applicationNameSearch && {
          applicationNameSearch: filterValues.applicationNameSearch,
        })}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={areFiltersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Notification Filters"
      >
        <NotificationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>
    </>
  );
};
